import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
function FormFields({ isEdit, form, setForm, outlets }) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={isEdit ? "eu-name" : "cu-name"}>Nama</Label>
        <Input id={isEdit ? "eu-name" : "cu-name"} required value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          data-testid={isEdit ? "edit-user-name-input" : "create-user-name-input"} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={isEdit ? "eu-email" : "cu-email"}>Email</Label>
        <Input id={isEdit ? "eu-email" : "cu-email"} type="email" required value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          data-testid={isEdit ? "edit-user-email-input" : "create-user-email-input"} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={isEdit ? "eu-pass" : "cu-pass"}>Password {isEdit && <span className="text-zinc-400 font-normal">(kosongkan jika tidak diubah)</span>}</Label>
        <Input id={isEdit ? "eu-pass" : "cu-pass"} type="password" required={!isEdit} value={form.password}
          onChange={e => setForm({ ...form, password: e.target.value })}
          data-testid={isEdit ? "edit-user-password-input" : "create-user-password-input"} placeholder="Min. 6 karakter" />
      </div>
      <div className="space-y-1.5">
        <Label>Outlet yang Dikelola</Label>
        <Select value={form.outlet_id} onValueChange={v => setForm({ ...form, outlet_id: v })}>
          <SelectTrigger data-testid={isEdit ? "edit-user-outlet-select" : "create-user-outlet-select"}>
            <SelectValue placeholder="Pilih outlet" />
          </SelectTrigger>
          <SelectContent>
            {outlets.map(o => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}


export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", outlet_id: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/outlets")]);
    setItems(u.data); setOutlets(o.data);
  };

  useEffect(() => { load(); }, []);

  if (user?.role !== "super_admin") {
    return <div className="text-sm text-zinc-500">Akses khusus Super Admin.</div>;
  }

  const resetForm = () => setForm({ name: "", email: "", password: "", outlet_id: "" });

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Akun Admin Outlet dibuat");
      setOpen(false); resetForm();
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal membuat akun");
    } finally { setSaving(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: form.name, email: form.email, outlet_id: form.outlet_id };
      if (form.password) body.password = form.password;
      await api.patch(`/users/${editing.id}`, body);
      toast.success("Akun diperbarui");
      setEditing(null); resetForm();
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal memperbarui akun");
    } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Akun dinonaktifkan" : "Akun diaktifkan");
      await load();
    } catch {
      toast.error("Gagal mengubah status akun");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", outlet_id: u.outlet_id || "" });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Pengguna</h1>
          <p className="text-sm text-zinc-500 mt-1">Kelola akun Admin Outlet.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-button">
              <Plus size={16} className="mr-2" /> Buat Admin Outlet
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Buat Admin Outlet</DialogTitle></DialogHeader>
            <form onSubmit={submitCreate} className="space-y-4">
              <FormFields isEdit={false} form={form} setForm={setForm} outlets={outlets} />
              <Button type="submit" disabled={saving || !form.outlet_id} className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-user-submit">
                {saving ? "Menyimpan..." : "Buat Akun"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nama</th>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Outlet</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Dibuat</th>
                <th className="text-left px-5 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-400">Belum ada Admin Outlet.</td></tr>}
              {items.map(u => (
                <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-5 py-3 text-zinc-600">{u.outlet_name || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-5 py-3">
                    {u.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Aktif</Badge>
                      : <Badge className="bg-zinc-200 text-zinc-600 border-0">Nonaktif</Badge>}
                  </td>
                  <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{u.created_at ? dayjs(u.created_at).format("DD MMM YYYY") : "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                        <Pencil size={13} className="mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm"
                        className={u.is_active ? "text-red-700 border-red-300" : "text-emerald-700 border-emerald-300"}
                        onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>
                        {u.is_active ? <><PowerOff size={13} className="mr-1" /> Nonaktifkan</> : <><Power size={13} className="mr-1" /> Aktifkan</>}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); resetForm(); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Admin Outlet</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <FormFields isEdit={true} form={form} setForm={setForm} outlets={outlets} />
            <Button type="submit" disabled={saving || !form.outlet_id} className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="edit-user-submit">
              {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
